import React, { useEffect, useState } from 'react';
import { AlertTriangle, XCircle, Calendar, CreditCard, X } from 'lucide-react';
import { APIConfig } from '../data/config/APIConfig';

interface PaymentStatus {
  status: 'paid' | 'pending' | 'overdue' | 'not_configured';
  payment_due_date: string | null;
  last_payment_date: string | null;
  days_until_due: number;
  is_blocked: boolean;
  organization_name: string;
  organization_id?: string;
  is_trial?: boolean;
  trial_ends_at?: string | null;
  trial_days?: number | null;
  trial_status?: 'active' | 'expiring_soon' | 'expired' | null;
  days_remaining_trial?: number | null;
}

interface PaymentWarningModalProps {
  onClose?: () => void;
}

const PaymentWarningModal: React.FC<PaymentWarningModalProps> = ({ onClose }) => {
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    checkPaymentStatus();
  }, []);

  const checkPaymentStatus = async () => {
    try {
      const api = APIConfig.getInstance();
      const response = await api.get('/api/v1/billing/status');

      const status = response.data;
      setPaymentStatus(status);

      // ── TRIAL: verificar se deve mostrar aviso ──
      if (status.is_trial && (status.trial_status === 'expiring_soon' || status.trial_status === 'expired')) {
        const today = new Date().toISOString().split('T')[0];
        const lastShownKey = `trial_warning_shown_${status.organization_id || 'default'}`;
        const lastShown = localStorage.getItem(lastShownKey);

        if (status.trial_status === 'expired' || lastShown !== today) {
          setShowModal(true);
          localStorage.setItem(lastShownKey, today);
        }
        return;
      }

      // ── PAGAMENTO: verificar se deve mostrar aviso ──
      const shouldShowWarning = 
        status.status === 'overdue' || 
        status.status === 'due_soon' ||
        (status.status === 'pending' && status.days_until_due !== null && status.days_until_due <= 5);
      
      if (shouldShowWarning) {
        // 🔒 CONTROLE: Só mostrar 1x por dia
        const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
        const lastShownKey = `payment_warning_shown_${status.organization_id || 'default'}`;
        const lastShown = localStorage.getItem(lastShownKey);
        
        // Se atrasado (overdue), SEMPRE mostrar (bloqueio)
        // Se pendente/aviso, só mostrar se não mostrou hoje
        if (status.status === 'overdue' || lastShown !== today) {
          setShowModal(true);
          
          // Salvar que mostrou hoje
          localStorage.setItem(lastShownKey, today);
        } else {
          console.log('💡 Aviso de pagamento já foi mostrado hoje');
        }
      }
    } catch (error: any) {
      // Se retornar 402 Payment Required, significa que está bloqueado
      if (error.response?.status === 402) {
        const errorCode = error.response.data.error_code;
        if (errorCode === 'TRIAL_EXPIRED') {
          setPaymentStatus({
            status: 'overdue',
            payment_due_date: null,
            last_payment_date: null,
            days_until_due: 0,
            is_blocked: false,
            organization_name: error.response.data.organization || 'Sua organização',
            is_trial: true,
            trial_ends_at: error.response.data.trial_ends_at,
            trial_status: 'expired',
            days_remaining_trial: 0,
          });
        } else {
          setPaymentStatus({
            status: 'overdue',
            payment_due_date: error.response.data.payment_due_date,
            last_payment_date: null,
            days_until_due: -error.response.data.days_overdue || 0,
            is_blocked: true,
            organization_name: error.response.data.organization || 'Sua organização',
          });
        }
        setShowModal(true); // Bloqueio sempre mostra
      }
      console.error('Erro ao verificar status de pagamento:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  const handleClose = () => {
    setShowModal(false);
    if (onClose) {
      onClose();
    }
  };

  // Determinar urgência baseada nos dias restantes
  const getUrgencyLevel = (days: number) => {
    if (days <= 1) return { level: 'critical', color: 'red', emoji: '🚨', message: 'URGENTE!' };
    if (days <= 3) return { level: 'high', color: 'orange', emoji: '⚠️', message: 'Atenção!' };
    return { level: 'medium', color: 'yellow', emoji: '📌', message: 'Aviso' };
  };

  if (loading || !showModal || !paymentStatus) {
    return null;
  }

  const urgency = getUrgencyLevel(paymentStatus.days_until_due);

  // ── Modal: Trial EXPIRADO (bloqueio total) ──
  if (paymentStatus.is_trial && paymentStatus.trial_status === 'expired') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-[9999] backdrop-blur-sm">
        <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6 border-4 border-amber-500">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-amber-100 mb-4">
              <span className="text-4xl">🧪</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Período de Teste Encerrado</h2>
            <p className="text-amber-600 font-semibold mb-4">{paymentStatus.organization_name}</p>

            <div className="bg-amber-50 rounded-lg p-4 mb-4 border border-amber-200">
              <p className="text-sm text-gray-700 leading-relaxed">
                O período de teste desta organização chegou ao fim.
                <br /><br />
                Para continuar usando o sistema, entre em contato com a AWFlux para fechar o contrato e ativar sua conta.
              </p>
            </div>

            <div className="space-y-2">
              <a
                href="mailto:comercial@awflux.com.br"
                className="w-full bg-amber-500 text-white px-6 py-3 rounded-lg hover:bg-amber-600 transition-colors font-medium flex items-center justify-center gap-2"
              >
                <CreditCard className="w-5 h-5" />
                Fechar Contrato
              </a>
              <button
                onClick={() => {
                  localStorage.removeItem('awflux_token');
                  window.location.href = '/login';
                }}
                className="w-full bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Fazer Logout
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Modal: Trial EXPIRANDO (aviso, sem bloqueio) ──
  if (paymentStatus.is_trial && paymentStatus.trial_status === 'expiring_soon') {
    const daysLeft = paymentStatus.days_remaining_trial ?? 0;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999] backdrop-blur-sm">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 border-2 border-amber-400">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center h-12 w-12 rounded-full bg-amber-100">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">⏳ Período de Teste</h2>
                <p className="text-sm font-medium text-amber-600">Encerrando em breve</p>
              </div>
            </div>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="bg-amber-50 rounded-lg p-4 mb-4 border border-amber-200">
            <div className="flex items-center justify-between">
              <span className="text-gray-700 font-medium">Dias restantes:</span>
              <span className="text-2xl font-bold text-amber-600">
                {daysLeft} {daysLeft === 1 ? 'dia' : 'dias'}
              </span>
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-gray-700 font-medium">Organização:</span>
              <span className="text-gray-900 font-semibold">{paymentStatus.organization_name}</span>
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-4 mb-6 border border-blue-200">
            <p className="text-sm text-gray-700 leading-relaxed">
              ⚠️ O período de teste encerra em <strong>{daysLeft} {daysLeft === 1 ? 'dia' : 'dias'}</strong>.
              Após esse prazo, o acesso ao sistema será bloqueado automaticamente.
              <br /><br />
              Entre em contato para fechar o contrato e manter o acesso.
            </p>
          </div>

          <div className="space-y-2">
            <a
              href="mailto:comercial@awflux.com.br"
              className="w-full bg-amber-500 text-white px-6 py-3 rounded-lg hover:bg-amber-600 transition-colors font-medium flex items-center justify-center gap-2"
            >
              <CreditCard className="w-5 h-5" />
              Fechar Contrato
            </a>
            <button
              onClick={handleClose}
              className="w-full bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              Continuar para o Sistema
            </button>
          </div>

          <p className="text-xs text-gray-500 text-center mt-4">
            Este aviso será exibido enquanto o período de teste estiver ativo.
          </p>
        </div>
      </div>
    );
  }

  // Modal de Bloqueio (Pagamento Atrasado)
  if (paymentStatus.status === 'overdue' || paymentStatus.is_blocked) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-[9999] backdrop-blur-sm">
        <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6 border-4 border-red-500">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
              <XCircle className="h-10 w-10 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Acesso Bloqueado</h2>
            <p className="text-red-600 font-semibold mb-4">Pagamento em Atraso</p>
            
            <div className="bg-red-50 rounded-lg p-4 mb-4 border border-red-200">
              <div className="space-y-2 text-left">
                <div className="flex justify-between">
                  <span className="text-gray-700 font-medium">Organização:</span>
                  <span className="text-gray-900 font-semibold">{paymentStatus.organization_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700 font-medium">Data de Vencimento:</span>
                  <span className="text-gray-900">{formatDate(paymentStatus.payment_due_date)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700 font-medium">Dias de Atraso:</span>
                  <span className="text-red-600 font-bold">
                    {Math.abs(paymentStatus.days_until_due)} dias
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 rounded-lg p-4 mb-6 border border-yellow-200">
              <p className="text-sm text-gray-700 leading-relaxed">
                ⚠️ O acesso ao sistema foi <strong>bloqueado</strong> devido ao atraso no pagamento.
                <br />
                <br />
                Entre em contato com o administrador para regularizar sua situação.
              </p>
            </div>

            <div className="space-y-2">
              <a
                href="mailto:suporte@awflux.com.br"
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2"
              >
                <CreditCard className="w-5 h-5" />
                Entrar em Contato
              </a>
              <button
                onClick={() => {
                  localStorage.removeItem('awflux_token');
                  window.location.href = '/login';
                }}
                className="w-full bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Fazer Logout
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Modal de Aviso (Vence em 5 dias ou menos)
  const borderColor = urgency.level === 'critical' ? 'border-red-500' : urgency.level === 'high' ? 'border-orange-500' : 'border-yellow-400';
  const bgColor = urgency.level === 'critical' ? 'bg-red-50' : urgency.level === 'high' ? 'bg-orange-50' : 'bg-yellow-50';
  const iconBg = urgency.level === 'critical' ? 'bg-red-100' : urgency.level === 'high' ? 'bg-orange-100' : 'bg-yellow-100';
  const iconColor = urgency.level === 'critical' ? 'text-red-600' : urgency.level === 'high' ? 'text-orange-600' : 'text-yellow-600';
  const textColor = urgency.level === 'critical' ? 'text-red-600' : urgency.level === 'high' ? 'text-orange-600' : 'text-yellow-600';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999] backdrop-blur-sm">
      <div className={`bg-white rounded-lg shadow-xl max-w-md w-full p-6 border-2 ${borderColor}`}>
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            <div className={`flex items-center justify-center h-12 w-12 rounded-full ${iconBg}`}>
              <AlertTriangle className={`h-6 w-6 ${iconColor}`} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{urgency.emoji} Aviso de Pagamento</h2>
              <p className={`text-sm font-medium ${textColor}`}>{urgency.message}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className={`${bgColor} rounded-lg p-4 mb-4 border ${borderColor.replace('border-', 'border-')}`}>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-gray-700 font-medium flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Data de Vencimento:
              </span>
              <span className="text-gray-900 font-semibold">
                {formatDate(paymentStatus.payment_due_date)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-700 font-medium">Dias Restantes:</span>
              <span className={`font-bold text-lg ${textColor}`}>
                {paymentStatus.days_until_due} {paymentStatus.days_until_due === 1 ? 'dia' : 'dias'}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 rounded-lg p-4 mb-6 border border-blue-200">
          <p className="text-sm text-gray-700 leading-relaxed">
            {urgency.level === 'critical' ? (
              <>🚨 <strong>ÚLTIMO DIA!</strong> Seu pagamento vence hoje/amanhã. Realize o pagamento IMEDIATAMENTE para evitar bloqueio do sistema!</>
            ) : urgency.level === 'high' ? (
              <>⚠️ Seu pagamento vence em <strong>{paymentStatus.days_until_due} dias</strong>. Antecipe o pagamento para evitar interrupção do serviço.</>
            ) : (
              <>📌 Seu pagamento vence em breve. Para evitar a <strong>interrupção do serviço</strong>, realize o pagamento o quanto antes.</>
            )}
            <br />
            <br />
            Após o vencimento, o acesso ao sistema será automaticamente bloqueado.
          </p>
        </div>

        <div className="space-y-2">
          <a
            href="mailto:financeiro@awflux.com.br"
            className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2"
          >
            <CreditCard className="w-5 h-5" />
            Falar com Financeiro
          </a>
          <button
            onClick={handleClose}
            className="w-full bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            Continuar para o Sistema
          </button>
        </div>

        <p className="text-xs text-gray-500 text-center mt-4">
          Este aviso será exibido automaticamente até a regularização do pagamento.
        </p>
      </div>
    </div>
  );
};

export default PaymentWarningModal;
